import { ErrorBoundary } from 'common/components/ErrorBoundary/ErrorBoundary'
import PageError from 'components/ErrorPages/PageError'
import {
  Modal,
  useModalState,
  Flex,
  Grid,
  GridItem
} from '@sequencehq/core-components'
import { Button } from '@sequencehq/core-components'
import { DefRevScheduleProgressGraph } from 'RevenueRecognition/view/deferredRevenueDashboard/content/recognitionDrawerLayouts/common/DefRevScheduleProgressGraph'
import { DeferredRevenueProgressVisualization } from '../common/DeferredRevenueProgressVisualization'
import { DeferredRevenueDetailDrawerSummary } from './DeferredRevenueDetailDrawerSummary'
import { DeferredRevenueDetailDrawerBreakdown } from './DeferredRevenueDetailDrawerBreakdown'
import { RecognizeRevenueModal } from './recognitionDrawerLayouts/milestone/RecognizeRevenueModal'
import { ProtectedButton } from 'lib/rbac'

type DeferredRevenueDetailDrawerProps = {
  selectedScheduleId: string
  canRecognizeRevenue: boolean
  closeDrawer: () => void
}

const DeferredRevenueDetailDrawerContent = (
  props: DeferredRevenueDetailDrawerProps
) => {
  const { isModalOpen, openModal, closeModal } = useModalState()

  return (
    <Grid
      gridTemplateAreas={`
    "header"
    "content"
    ${props.canRecognizeRevenue ? '"footer"' : ''}
    `}
      gridTemplateRows={`
          auto
          1fr
          ${props.canRecognizeRevenue ? 'auto' : ''}
        `}
      overflow="hidden"
      height="100%"
      width="100%"
    >
      <GridItem gridArea="header">
        <Modal.Header showCloseButton withBorder>
          Overview
        </Modal.Header>
      </GridItem>
      <GridItem gridArea="content" overflow="auto">
        <ErrorBoundary
          name="DeferredRevenueDetailDrawer.content"
          fallback={PageError}
        >
          <Modal.Content>
            <Flex flexDirection="column" gap="24px">
              <DeferredRevenueProgressVisualization
                defRevScheduleId={props.selectedScheduleId}
              />
              <DeferredRevenueDetailDrawerSummary
                defRevScheduleId={props.selectedScheduleId}
              />
              <DeferredRevenueDetailDrawerBreakdown
                defRevScheduleId={props.selectedScheduleId}
              />
              <DefRevScheduleProgressGraph
                defRevScheduleId={props.selectedScheduleId}
              />
            </Flex>
          </Modal.Content>
        </ErrorBoundary>
      </GridItem>
      {props.canRecognizeRevenue && (
        <GridItem gridArea="footer">
          <Modal.Footer>
            <ProtectedButton permission="org.revenueRecognition:update">
              <Button
                style={{
                  width: '100%'
                }}
                onClick={openModal}
                variant="secondary"
                data-testid="revrec.defrevDetail.modal.milestoneRecognition.new"
              >
                Recognize revenue
              </Button>
            </ProtectedButton>
            <RecognizeRevenueModal
              defRevScheduleId={props.selectedScheduleId}
              isOpen={isModalOpen}
              onClose={closeModal}
              onSuccess={props.closeDrawer}
            />
          </Modal.Footer>
        </GridItem>
      )}
    </Grid>
  )
}

export const DeferredRevenueDetailDrawer = ({
  selectedScheduleId,
  closeDrawer,
  canRecognizeRevenue
}: DeferredRevenueDetailDrawerProps & { closeDrawer: () => void }) => {
  if (!selectedScheduleId) {
    return null
  }

  return (
    <Modal
      isOpen
      onClose={closeDrawer}
      variant="drawer"
      data-testid="revrec.deferredRevenueDetailDrawer"
    >
      <ErrorBoundary name="DeferredRevenueDetailDrawer" fallback={PageError}>
        <DeferredRevenueDetailDrawerContent
          selectedScheduleId={selectedScheduleId}
          canRecognizeRevenue={canRecognizeRevenue}
          closeDrawer={closeDrawer}
        />
      </ErrorBoundary>
    </Modal>
  )
}
